import { useState } from 'react';
import { api } from '../../../api/client';
import { Package, Link, Unlink, Info, Check, X } from 'lucide-react';
import { useCan, Can } from '../../../lib/rbac';

interface StockTabProps {
  product: any;
  sellerId: number;
  onRefresh: () => void;
}

export default function StockTab({ product, sellerId, onRefresh }: StockTabProps) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [editing, setEditing] = useState(false);
  const [newQty, setNewQty] = useState<number>(product.available_quantity ?? 0);
  const [saving, setSaving] = useState(false);

  const isLinked = product.cost_source === 'internal_stock';

  const handleSaveStock = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/catalog/products/${product.item_id}/ml/stock?seller_id=${sellerId}`, {
        quantity: newQty,
      });
      if (res.success) {
        onRefresh();
        setEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setNewQty(product.available_quantity ?? 0);
    setEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* ML Stock Section */}
      <div className="rounded-lg border border-[var(--color-border)] p-4">
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
          <Package size={16} />
          Stock en MercadoLibre
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Available Quantity - Editable */}
          <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">Cantidad disponible</div>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  value={newQty}
                  onChange={(e) => setNewQty(parseInt(e.target.value) || 0)}
                  className="w-24 px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                  autoFocus
                />
                <button
                  onClick={handleSaveStock}
                  disabled={saving}
                  className="p-1 rounded hover:bg-green-500/20 text-green-500 transition-colors"
                  title="Guardar"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="p-1 rounded hover:bg-red-500/20 text-red-500 transition-colors"
                  title="Cancelar"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-[var(--color-text)]">
                  {product.available_quantity ?? 0}
                </span>
                <Can cap="catalog.edit_stock">
                  <button
                    onClick={() => {
                      setNewQty(product.available_quantity ?? 0);
                      setEditing(true);
                    }}
                    className="text-xs px-2 py-0.5 rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-text-muted)] transition-colors"
                  >
                    Editar
                  </button>
                </Can>
              </div>
            )}
          </div>

          {/* Sold Quantity - Read Only */}
          <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">Cantidad vendida</div>
            <span className="text-lg font-semibold text-[var(--color-text)]">
              {product.sold_quantity ?? 0}
            </span>
          </div>
        </div>
      </div>

      {/* Internal Stock Section */}
      <div className="rounded-lg border border-[var(--color-border)] p-4">
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
          {isLinked ? <Link size={16} /> : <Unlink size={16} />}
          Stock Interno
        </h3>

        {isLinked ? (
          <>
            <div className="mb-4">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/15 text-green-500 border border-green-500/30">
                <Link size={12} />
                Vinculado a stock interno
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Cantidad disponible', value: product.internal_qty_on_hand != null ? String(product.internal_qty_on_hand) : null },
                { label: 'SKU Interno', value: product.internal_sku },
                { label: 'Proveedor', value: canMoney ? product.supplier_name : null },
                { label: 'Costo unitario', value: canMoney && product.internal_cost != null ? `$${product.internal_cost.toFixed(2)}` : null },
              ].map((f) => (
                <div key={f.label} className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
                  <div className="text-xs text-[var(--color-text-muted)] mb-0.5">{f.label}</div>
                  <div className="text-sm font-medium text-[var(--color-text)]">{f.value || '--'}</div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              Para editar el stock interno, ir a Inventario &gt; Stock Interno.
            </p>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="mb-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] border border-[var(--color-border)]">
                <Unlink size={12} />
                Sin stock interno vinculado
              </span>
            </div>
            <Can cap="purchases.operate_internal_stock">
              <button
                onClick={() => alert('Ir a Inventario > Stock Interno para vincular')}
                className="mt-2 px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-text-muted)] transition-colors"
              >
                Vincular a producto interno
              </button>
            </Can>
          </div>
        )}
      </div>

      {/* Manual Stock Info */}
      <div className="rounded-lg border border-[var(--color-border)] p-4">
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <Info size={16} />
          Stock Manual
        </h3>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              product.manual_stock
                ? 'bg-yellow-500/15 text-yellow-500 border border-yellow-500/30'
                : 'bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] border border-[var(--color-border)]'
            }`}
          >
            {product.manual_stock ? 'Activo' : 'Inactivo'}
          </span>
        </div>
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          Cuando Stock Manual esta activo, el Price Monitor no gestiona el stock de este item.
          El stock se controla manualmente o a traves del sistema de stock interno.
        </p>
      </div>
    </div>
  );
}
